import { Stack, Typography } from '@mui/material';
import React, { memo, useCallback, useMemo, useState } from 'react';
import useConnectedApps from '../../hooks/useConnectedApps';
import theme from '../../theme';

type LogoState = 'clearbit' | 'google' | 'failed';

const DynamicAppIcon: React.FC<{
    /** The app link — primary source for icon resolution. */
    link?: string | null;
    /** The app name — used as fallback when link doesn't match, and for initials. */
    name?: string;
    size?: number;
    showBackground?: boolean;
    /** When true, render the icon in grayscale with reduced opacity. */
    grayscale?: boolean;
}> = ({ link, name, size = 28, showBackground = true, grayscale = false }) => {
    const [logoState, setLogoState] = useState<LogoState>('clearbit');
    const { resolveApp, getAppInitials, getAppLogoUrl, getGoogleFaviconUrl } = useConnectedApps();

    const resolved = useMemo(() => resolveApp(link, name), [link, name, resolveApp]);

    const handleError = useCallback(() => {
        setLogoState(prev => prev === 'clearbit' ? 'google' : 'failed');
    }, []);

    const handleLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        if (img.naturalWidth <= 1 || img.naturalHeight <= 1) {
            handleError();
        }
    }, [handleError]);

    const grayscaleStyle: React.CSSProperties = grayscale
        ? { filter: 'grayscale(100%)', opacity: 0.45, transition: 'filter 0.2s, opacity 0.2s' }
        : {};

    // Bundled SVG icon available
    if (resolved.icon) {
        const IconComponent = resolved.icon;

        if (!showBackground) {
            return <span style={grayscaleStyle}><IconComponent style={{ width: size, height: size }} /></span>;
        }

        return (
            <Stack alignItems="center" justifyContent="center"
                sx={{
                    width: size,
                    height: size,
                    minWidth: size,
                    borderRadius: '50%',
                    background: theme.colors.neutral['100'],
                    ...grayscaleStyle,
                }}>
                <IconComponent style={{ width: size * 0.6, height: size * 0.6 }} />
            </Stack>
        );
    }

    // Image URL icon (e.g. Attio — SVG with XML declaration that can't be ReactComponent)
    if (resolved.iconUrl) {
        const imgStyle = { width: size * 0.6, height: size * 0.6, objectFit: 'contain' as const };

        if (!showBackground) {
            return <img src={resolved.iconUrl} alt={resolved.name} style={{ width: size, height: size, objectFit: 'contain', ...grayscaleStyle }} />;
        }

        return (
            <Stack alignItems="center" justifyContent="center"
                sx={{
                    width: size,
                    height: size,
                    minWidth: size,
                    borderRadius: '50%',
                    background: theme.colors.neutral['100'],
                    overflow: 'hidden',
                    ...grayscaleStyle,
                }}>
                <img src={resolved.iconUrl} alt={resolved.name} style={imgStyle} />
            </Stack>
        );
    }

    // No bundled icon — try fetching from the web using the resolved domain
    if (logoState !== 'failed' && resolved.domain) {
        const logoUrl = logoState === 'clearbit'
            ? getAppLogoUrl(resolved.domain)
            : getGoogleFaviconUrl(resolved.domain);

        const imgStyle = showBackground
            ? { width: size * 0.6, height: size * 0.6, objectFit: 'contain' as const }
            : { width: size, height: size, borderRadius: '50%', objectFit: 'contain' as const };

        const imgEl = (
            <img
                src={logoUrl}
                alt={resolved.name}
                onError={handleError}
                onLoad={handleLoad}
                style={imgStyle}
            />
        );

        if (!showBackground) return <span style={grayscaleStyle}>{imgEl}</span>;

        return (
            <Stack alignItems="center" justifyContent="center"
                sx={{
                    width: size,
                    height: size,
                    minWidth: size,
                    borderRadius: '50%',
                    background: theme.colors.neutral['100'],
                    overflow: 'hidden',
                    ...grayscaleStyle,
                }}>
                {imgEl}
            </Stack>
        );
    }

    // Fallback: colored circle with initials
    const initials = getAppInitials(resolved.name || '?');
    const fontSize = size * 0.35;

    return (
        <Stack alignItems="center" justifyContent="center"
            sx={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: '50%',
                background: resolved.color,
                ...grayscaleStyle,
            }}>
            <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize,
                color: '#fff',
                lineHeight: 1,
                userSelect: 'none',
            }}>
                {initials}
            </Typography>
        </Stack>
    );
};

export default memo(DynamicAppIcon);
